import { create } from 'zustand';
import { AIService } from 'src/services/ai';
import { applicationService } from 'src/services/application';
import {
  EstimationRequest,
  EstimationResponse,
} from 'src/services/interfaces/ai';
import { Application } from 'src/services/interfaces/application';

import { useApplicationStore } from './application';
import { useInsuranceStore } from './insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';

interface FinalSubmissionState {
  // Loading states
  isUnderwritingLoading: boolean;
  isSubmittingApplication: boolean;

  // AI underwriting result
  underwritingResult: EstimationResponse | null;
  underwritingError: string | null;

  // Application submission
  submissionError: string | null;
  isSubmitted: boolean;

  // Actions
  runUnderwriting: (data: EstimationRequest | Application) => Promise<void>;
  submitFinalApplication: (data: Application, token?: string) => Promise<void>;
  resetState: () => void;
  clearErrors: () => void;
  getEstimation: (id: number | string) => Promise<void>;
}

export const useFinalSubmissionStore = create<FinalSubmissionState>(
  (set, get) => ({
    isUnderwritingLoading: false,
    isSubmittingApplication: false,
    underwritingResult: null,
    underwritingError: null,
    submissionError: null,
    isSubmitted: false,

    getEstimation: async (id: number | string) => {
      set({ isUnderwritingLoading: true });
      try {
        const result = await applicationService.getEstimation(id);
        await applicationService.applicationState();
        if (result) {
          useApplicationStore.getState().setAcceptedTier(result.tier);
        }
        console.log('Estimation result:', result);

        if (result.status === 'rejected') {
          const rejectionReason = result.details?.reason;
          const isApiErrorRejection = rejectionReason === 'API Error';
          const currentState = useApplicationStore.getState();
          const currentApplication = currentState.applications[0];

          if (!isApiErrorRejection) {
            useApplicationStore.getState().setIsMainPlanRejected(true);
            useOnboardingStore.getState().rejectMedicalPlan();
          }

          if (
            !isApiErrorRejection &&
            currentApplication &&
            currentApplication.plans.length > 0
          ) {
            const fullPlans = useInsuranceStore.getState().plans;

            console.log('Full plans:', fullPlans);

            const rejectedPlanId = result?.priceId;
            console.log('Rejected plan ID:', rejectedPlanId);

            try {
              const data = useApplicationStore.getState().applications[0];
              const fullPlans = useInsuranceStore.getState().plans;
              const newPlans = [];
              if (fullPlans.length > 0) {
                const dataPlans = [...data.plans];
                for (const plan of dataPlans) {
                  const myMajorPlan = fullPlans
                    .find(
                      (p) => p.category === 'Major Medical' && p.id === plan.id
                    )
                    ?.prices.find((item) => item.tier === plan.tier);
                  if (!myMajorPlan) {
                    newPlans.push(plan);
                  }
                }
                data.plans = dataPlans;
              }
              data.plans = newPlans;
            } catch (error) {
              console.log('Error:', error);
            }

            // if (rejectedPlanId) {
            //   const rejectedPlan = fullPlans.find(
            //     (plan) => plan.id === rejectedPlanId
            //   );

            //   if (rejectedPlan) {
            //     const updatedPlans = currentApplication.plans.filter(
            //       (plan) => plan.id !== rejectedPlan.id
            //     );
            //     console.log(1)
            //     await useApplicationStore.getState().updateApplication({
            //       ...currentApplication,
            //       plans: updatedPlans,
            //     });
            //   }
            // }
          }
        }

        set({
          underwritingResult: result,
          isUnderwritingLoading: false,
        });
      } catch (error: any) {
        const rejectResult: EstimationResponse = {
          status: 'rejected',
          details: {
            reason: 'API Error',
            error:
              error?.response?.data?.message ||
              error?.response?.data?.errors?.[0]?.message ||
              error?.message ||
              'Failed to get estimation',
          },
        };

        // No need to update application plans on estimation error
        // The error will be handled by the UI

        set({
          underwritingResult: rejectResult,
          underwritingError: 'Failed to get estimation',
          isUnderwritingLoading: false,
        });
      }
    },

    runUnderwriting: async (data: EstimationRequest | Application) => {
      set({ isUnderwritingLoading: true, underwritingError: null });

      try {
        const result = await AIService.runEstimation(data);
        set({
          underwritingResult: result,
          isUnderwritingLoading: false,
        });
      } catch (error: unknown) {
        const anyError: any = error as any;
        const statusCode = anyError?.response?.status;

        if (statusCode === 400 || statusCode === 422) {
          const apiDetail = anyError?.response?.data?.detail;
          const firstValidation =
            Array.isArray(apiDetail) && apiDetail[0]?.msg
              ? apiDetail[0].msg
              : undefined;
          const validationMessage =
            firstValidation ||
            (typeof apiDetail === 'string' && apiDetail) ||
            'Incorrect data for calculation';

          set({
            underwritingResult: null,
            underwritingError: validationMessage,
            isUnderwritingLoading: false,
          });
          return;
        }

        const combinedMessage =
          anyError?.response?.data?.message ||
          (error instanceof Error
            ? error.message
            : 'Unexpected error occurred');

        const rejectResult: EstimationResponse = {
          status: 'rejected',
          details: {
            reason: 'API Error',
            error: combinedMessage,
          },
        } as any;

        set({
          underwritingResult: rejectResult,
          underwritingError: combinedMessage,
          isUnderwritingLoading: false,
        });
      }
    },

    submitFinalApplication: async (data: Application, token?: string) => {
      set({ isSubmittingApplication: true, submissionError: null });

      try {
        const currentApp = useApplicationStore.getState().applications[0];
        const applicationId =
          useApplicationStore.getState().currentApplicationId;

        if (!currentApp || !applicationId) {
          return;
        }

        if (token) {
          console.log('Submitting application with payment token:', token);
          await applicationService.submitApplication(applicationId, {
            paymentMethodId: token,
          });
        } else {
          console.log('No payment token available, submitting without token');
          await applicationService.submitApplication(applicationId, {
            paymentMethodId: '',
          });
        }

        useApplicationStore.setState({
          hasCompletedApplication: true,
          isNewApplicant: false,
        });

        await Promise.all([
          useApplicationStore.getState().fetchActiveSubscriptionPlans(),
          useApplicationStore.getState().getCurrentApplication(),
        ]);

        set({
          isSubmitted: true,
          isSubmittingApplication: false,
        });
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unexpected error occurred. Please try later.';
        set({
          submissionError: errorMessage,
          isSubmittingApplication: false,
        });
      }
    },

    resetState: () => {
      set({
        isUnderwritingLoading: false,
        isSubmittingApplication: false,
        underwritingResult: null,
        underwritingError: null,
        submissionError: null,
        isSubmitted: false,
      });
    },

    clearErrors: () => {
      set({
        underwritingError: null,
        submissionError: null,
      });
    },
  })
);
